import { useTranslation } from 'react-i18next';
import { useFriend } from '@/hooks/contacts/use-friends';
import useGroupInfo from '@/hooks/groups/use-group-info';
import { generateJoinApplicationRemark } from '@/hooks/groups/use-group-join-applications';
import { ConversationMessageContentType, type ConversationMessageTypeUnion } from '@/models/conversations/conversation';
import { transformMessageTextByType } from '@/utils/transform-message-text-by-type';
import { translateComplexKey } from '@/utils/translate';
import type { ContentCommonProps } from './content-common-props';

type Props = Pick<ContentCommonProps, 'message'>;

export default function ContentAnnouncement({ message }: Props) {
  const isGroup = 'GroupID' in message;
  const { t } = useTranslation();
  const { canManage } = useGroupInfo(isGroup ? message.GroupID : null);
  const { friend } = useFriend(message.UserID);

  const transformMessage = (message: ConversationMessageTypeUnion): string => {
    if (!message.Message.includes('|')) {
      return transformMessageTextByType(message, friend?.RemarkName);
    }

    if (message.MsgType === ConversationMessageContentType.JoinGroup) {
      const [originalMessage = '', newcomerName, source, remark] = message.Message.split('|');

      let content = t('message.join', { user: friend?.RemarkName || friend?.FriendNickName || newcomerName });
      if (canManage) {
        content += ` (${translateComplexKey(generateJoinApplicationRemark(source, remark, originalMessage), t)})`;
      }

      return content;
    }

    if (message.MsgType === ConversationMessageContentType.Notice) {
      const [content = ''] = message.Message.split('|');
      return content;
    }

    return message.Message;
  };

  if (!canManage && message.MsgType === ConversationMessageContentType.LeaveGroup) return null;

  return (
    <div
      key={message.ID}
      className='my-3 w-full text-center text-sm text-neutral-950'>
      {transformMessage(message)}
    </div>
  );
}
